import React, { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import Button from "../../common/Button";
import { Link } from "react-router-dom";
import karmatekEventScheduleData from "../../../data/karmatekEventScheduleData";
import type { EventRecord } from "../../../types/dataTypes";
import { useScrambleText } from "../../../hooks/useScrambleText";

// Extract Robo Events from schedule
const roboEventIDs = ["robo-race", "robo-soccer"];
const allEvents = (karmatekEventScheduleData.days || []).flatMap(
  (day) => day.events || [],
);

type Props = {
  events?: EventRecord[];
};

const RoboEventCard: React.FC<{ event: any }> = ({ event }) => {
  const { ref: inViewRef, inView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  const [poweredId, setPoweredId] = useState<string | null>(null);

  const { displayedText: scrambledTitle } = useScrambleText({
    text: event.name || "",
    enabled: inView,
    speed: 60,
  });

  const timeDisplay =
    event.startTime && event.endTime
      ? `${event.date}, ${event.startTime} – ${event.endTime}`
      : event.startTime
        ? `${event.date}, ${event.startTime}`
        : event.date;

  return (
    <div
      ref={inViewRef}
      key={event.id || event.slug}
      className="col-12 col-sm-6 col-lg-6 d-flex mb-4"
    >
      <div
        className={`rounded-4 shadow-lg text-white w-100 position-relative overflow-hidden karmatek-power-card ${
          poweredId === event.id ? "is-powered" : ""
        }`}
        style={{
          minHeight: "420px",
        }}
        onClick={() =>
          setPoweredId((prev) => (prev === event.id ? null : event.id))
        }
        role="button"
        aria-pressed={poweredId === event.id}
        tabIndex={0}
        onKeyDown={(eventKey) => {
          if (eventKey.key === "Enter" || eventKey.key === " ") {
            eventKey.preventDefault();
            setPoweredId((prev) => (prev === event.id ? null : event.id));
          }
        }}
      >
        {/* Layer 1: Background Image - Cinematic Scene Setter */}
        {event.backgroundImage && (
          <motion.div
            initial={{ opacity: 0, scale: 1.05 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{
              duration: 0.7,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="position-absolute top-0 start-0 w-100 h-100"
            style={{
              backgroundImage: `url(${event.backgroundImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              zIndex: 1,
            }}
          />
        )}

        <div
          className="position-absolute top-0 start-0 w-100 h-100"
          style={{
            background:
              "linear-gradient(to top, rgba(0,0,0,0.85), rgba(0,0,0,0.4))",
            zIndex: 2,
          }}
        />

        {/* Layer 2: Text Content - Information Reveal */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{
            duration: 0.6,
            delay: 0.15,
            ease: [0.22, 1, 0.36, 1],
          }}
          className="position-relative z-3 p-4 d-flex flex-column justify-content-between h-100"
        >
          <div>
            {/* Title with Scramble Reveal */}
            <motion.h3
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{
                duration: 0.3,
                delay: 0.3,
              }}
              className="fw-bold"
            >
              {scrambledTitle}
            </motion.h3>

            {/* Subheading - Line by Line Fade */}
            {event.subheading && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{
                  duration: 0.4,
                  delay: 0.4,
                }}
                className="text-info text-uppercase fw-semibold"
              >
                {event.subheading}
              </motion.div>
            )}

            {/* Tagline - Line by Line Fade */}
            {event.tagline && (
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{
                  duration: 0.4,
                  delay: 0.45,
                }}
                className="mt-2"
              >
                {event.tagline}
              </motion.p>
            )}
          </div>

          <div className="d-flex flex-column align-items-center gap-2 mt-3">
            {/* Time Badge with Micro Scale Pop */}
            <motion.span
              initial={{ opacity: 0, scale: 0.95 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{
                duration: 0.3,
                delay: 0.5,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="badge bg-info text-dark px-3 py-2 rounded-pill fw-semibold"
            >
              {timeDisplay}
            </motion.span>

            {/* Chips with Stagger */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ delay: 0.55 }}
              className="d-flex flex-wrap gap-2 mt-2 w-100 justify-content-center"
            >
              {event.entryFee && (
                <motion.span
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  transition={{
                    duration: 0.3,
                    delay: 0.55,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="badge bg-dark text-light px-3 py-2 rounded-pill"
                >
                  Entry: {event.entryFee}
                </motion.span>
              )}
              {event.prizePool && (
                <motion.span
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  transition={{
                    duration: 0.3,
                    delay: 0.6,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="badge bg-dark text-light px-3 py-2 rounded-pill"
                >
                  Prize: {event.prizePool}
                </motion.span>
              )}
              {(event as any).mode && (
                <motion.span
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  transition={{
                    duration: 0.3,
                    delay: 0.65,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="badge bg-secondary text-light px-3 py-2 rounded-pill"
                >
                  {(event as any).mode}
                </motion.span>
              )}
            </motion.div>

            {/* Button with Micro Scale Pop */}
            {(event.slug || event.id) && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{
                  duration: 0.3,
                  delay: 0.7,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <Link
                  to={`/karmatek/event/${event.slug || event.id}`}
                  aria-label={`View details for ${event.name}`}
                  className="text-decoration-none"
                >
                  <Button text="View Details" />
                </Link>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const RoboEventsShowcase: React.FC<Props> = ({ events }) => {
  const sourceEvents = events?.length ? events : (allEvents as EventRecord[]);
  const roboEvents = sourceEvents.filter((event: any) =>
    roboEventIDs.includes(event.id),
  );

  return (
    <section className="text-center mt-5 mb-2 karmatek-district karmatek-district--robo">
      <div className="container">
        <div className="row justify-content-center">
          {roboEvents.map((event: any) => (
            <RoboEventCard key={event.id || event.slug} event={event} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default RoboEventsShowcase;
